import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import GiftMessageModal from "@/components/giftExperience/GiftMessageModal";
import Unboxing from "@/components/giftExperience/Unboxing";
import ProductDetailSlideIn from "@/components/products/ProductDetailSlideIn";
import { Confetti } from "@/components/redemption/experiential/Confetti";
import { primeConfettiAudio } from "@/components/redemption/experiential/confettiAudio";
import GiftLinkUnavailable from "@/components/redemption/GiftLinkUnavailable";
import GiftVideoModal from "@/components/redemption/GiftVideoModal";
import RedemptionNotYetActive from "@/components/redemption/RedemptionNotYetActive";
import RedemptionSuccess from "@/components/redemption/RedemptionSuccess";
import { resolveSchemeColors } from "@/constants/landing";
import { GIFT_FEED_PAGE_SIZE } from "@/constants/redemption";
import { useGiftContext } from "@/hooks/useGiftContext";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { useRedemptionProductDetail } from "@/hooks/useRedemptionProductDetail";
import { useRedemptionProducts } from "@/hooks/useRedemptionProducts";
import type { Product } from "@/interfaces/product";
import type { CustomLocation, RecipientFormDraft } from "@/interfaces/redemption";
import { resolveCountryCodeOrUs } from "@/lib/country";
import { resolveCountryFeed } from "@/utils/product-helper";
import { buildRecipientHeadline, redemptionLinkError } from "@/utils/redemption-helper";
import {
  clearRedemptionFlowState,
  loadRedemptionFlowState,
  saveRedemptionFlowState,
} from "@/utils/redemption-persistence-helper";
import { productForVariant } from "@/utils/variantResolution";
import RedemptionDetails from "./RedemptionDetails";
import RedemptionLanding from "./RedemptionLanding";

/**
 * Recipient redemption flow — the REAL public, token-authenticated entry
 * (`/g/:token`). Mounted OUTSIDE RequireAuth/RoleGuard in App.tsx: recipients are
 * not Clerk users; the opaque `:token` in the URL is the credential.
 *
 * Our own, incrementally-built flow (distinct from the prototype's
 * `RedemptionFlow.tsx`, kept as reference). It resolves the token → gift context
 * and drives the stages: the unbox splash (`Unboxing`, fullscreen) → the branded
 * `RedemptionLanding` when the unbox video ends. This component owns the screen-covering
 * overlays (gift message + gift video modals, the product-detail slide-in, confetti) so
 * they sit above whichever stage is active. It also starts the product-feed fetch (and warms the
 * first page of card images) as soon as the gift context resolves, so on the unbox path the grid
 * is usually in cache when the landing mounts. Recipient details / message land in later tickets.
 */
export default function Redemption() {
  const { token } = useParams<{ token: string }>();
  const { data: gift, isLoading, isError, error } = useGiftContext(token);
  const isMobile = useIsMobile();

  // Resume support: read any persisted flow state ONCE (sessionStorage, keyed by token) so a
  // browser refresh drops the recipient back on the step they were on, with their gift choice and
  // typed shipping draft intact, instead of restarting at the unbox intro. Seeds the state below.
  const [restored] = useState(() => (token ? loadRedemptionFlowState(token) : null));

  // Shared modal + trigger state, owned here so the modals live above every stage.
  const [giftMsgOpen, setGiftMsgOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  // Incrementing dissolves the unbox intro + plays the video (e.g. the gift-message
  // modal's "Unwrap Gift" button on the unboxing stage).
  const [playTrigger, setPlayTrigger] = useState(0);
  // Incremented to fire the celebratory confetti (persistent <Confetti/> below).
  const [confettiTrigger, setConfettiTrigger] = useState(0);
  // Flow stage. Opens on the unbox splash; advances to the landing when the unbox
  // video ends; "Choose this gift" advances to the recipient-details form (RFW-7),
  // and a successful redeem lands on the "redeemed" terminus (RFW-8 fills in the
  // thank-you + impact tail).
  const [stage, setStage] = useState<"unboxing" | "landing" | "details" | "redeemed">(() => {
    // Resume the stored stage, but never land on "details" without a chosen gift to render
    // (a corrupt/partial blob) — fall back to the landing in that case.
    if (restored?.stage === "details" && !restored.chosenProduct) return "landing";
    return restored?.stage ?? "unboxing";
  });
  // The submit itself 410'd (spent after the page loaded) — render only the redeemed page.
  const [alreadyRedeemedAtSubmit, setAlreadyRedeemedAtSubmit] = useState(false);
  // The contact claimed by the redeem POST, carried to the success screen so the thank-you note can
  // be attributed to it — required for a Universal Link, whose shared token has no bound contact.
  const [redeemedContactId, setRedeemedContactId] = useState<number | undefined>(undefined);
  // Envelope hand-off (RFW-8): once the fold-and-fly finishes, keep the details
  // view mounted (a blank white surface after its staged fade) UNDER the
  // confirmation as it fades in, so the cross-fade never flashes the background.
  // Cleared a beat later, once the confirmation is fully up.
  const [tailCrossfade, setTailCrossfade] = useState(false);
  // Unbox hand-off: when the unbox video ends, mount the landing UNDERNEATH and keep
  // the unbox layer on top fading out (holding the video's last frame). Cleared once
  // the fade finishes, unmounting it — so the landing is revealed by a crossfade
  // instead of a hard cut that flashes the video's first frame.
  const [unboxLeaving, setUnboxLeaving] = useState(false);
  // Product-detail slide-in — a screen-covering overlay owned here (like the modals),
  // above whichever stage is active. A card tap in the collection sets the selected id,
  // which drives the token-scoped detail fetch (the feed payload is a light subset);
  // null = closed.
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const productDetail = useRedemptionProductDetail(token ?? "", selectedProductId);
  // The chosen gift, snapshotted from the detail slide-in on "Choose this gift" (so it
  // survives closing the overlay), plus the exact variant to redeem and the recipient
  // form draft (preserved across a "Back to gifts" round-trip).
  const [chosenProduct, setChosenProduct] = useState<Product | null>(
    () => restored?.chosenProduct ?? null,
  );
  const [chosenVariantId, setChosenVariantId] = useState(() => restored?.chosenVariantId ?? "");
  // Well Told map products: the custom location chosen in the slide-in (null for any other gift).
  const [chosenLocation, setChosenLocation] = useState<CustomLocation | null>(
    () => restored?.chosenLocation ?? null,
  );
  const [recipientDraft, setRecipientDraft] = useState<RecipientFormDraft | null>(
    () => restored?.recipientDraft ?? null,
  );
  // Destination country for the "Shipping to" feed filter + the address stage. Editable via the
  // feed picker only when the country filter is unlocked (campaign created with a Pro+ account →
  // gift.shipping.locked === false). Lifted here so the feed and the address form always agree.
  //
  // Pre-selected from the RECIPIENT's own country (gift.shipping.country, seeded from Step 2),
  // homologated to a valid ISO code or US — NOT the campaign's Step 1 picker. Derived (with a
  // recipient override) rather than seeded via an effect, so the feed opens on the right country
  // with no US flash; once the recipient picks another country, that choice wins.
  const [countryOverride, setCountryOverride] = useState<string | null>(
    () => restored?.countryOverride ?? null,
  );
  // A "not yet active" manual link carries no gift context (no shipping) — fall back to US there;
  // this value is only actually consumed on the stages that render after that early return.
  const activeCountry =
    countryOverride ??
    resolveCountryCodeOrUs(
      gift && !("notYetActive" in gift) && !("limitReached" in gift)
        ? gift.shipping?.country
        : undefined,
    );

  // Prefetch the gift feed once the gift context resolves (same query as the landing and
  // collection, so they read it from cache) — spent or invalid links never fetch it. On the unbox
  // path the grid is usually ready when the landing mounts; with skip-unboxing or a slow network
  // the landing still shows its loading line and holds the pledge banner + footer until the feed
  // settles. Then warm the first page of card images at low priority so they don't compete with
  // the unbox video.
  const { data: feed } = useRedemptionProducts(gift ? token : undefined);
  useEffect(() => {
    if (!feed) return;
    const firstPage = resolveCountryFeed(
      feed.collections,
      activeCountry,
      feed.feedEdits[activeCountry],
      feed.addedProducts,
    ).slice(0, GIFT_FEED_PAGE_SIZE);
    for (const product of firstPage) {
      if (!product.imageUrl) continue;
      const img = new Image();
      img.fetchPriority = "low";
      img.src = product.imageUrl;
    }
  }, [feed, activeCountry]);

  // Celebratory confetti fires on the LANDING "arrival" — the moment the branded landing is first
  // shown — NOT the unbox video end. This way it fires whether the recipient unboxed OR the
  // campaign skips unboxing (straight to gift selection). Once per session (a "Back to gifts"
  // return to the landing shouldn't re-celebrate). Starts "already fired" when we resumed a
  // persisted session, so a browser refresh mid-flow doesn't replay the celebration.
  const confettiFiredRef = useRef(restored != null);
  useEffect(() => {
    // A dormant ("not yet active") or used-up ("limit reached") link has no gift experience, no confetti.
    if (!gift || "notYetActive" in gift || "limitReached" in gift || confettiFiredRef.current)
      return;
    const landingShown = stage === "landing" || (stage === "unboxing" && gift.config.skipUnboxing);
    if (landingShown && gift.config.confetti?.enabled !== false) {
      confettiFiredRef.current = true;
      // Best-effort audio unlock at landing render — the ONLY shot on the skip path (no unwrap
      // gesture there). Where the browser permits autoplay it plays; on a cold iOS load it stays
      // silent until a tap (browser policy). The unwrap tap still primes it on the unbox path.
      primeConfettiAudio();
      setConfettiTrigger((t) => t + 1);
    }
  }, [gift, stage]);

  // Persist the resumable slice on every change so a browser refresh resumes it (see
  // loadRedemptionFlowState). Only the two mid-flow stages are stored — the unbox intro has nothing
  // worth keeping, and the redeemed terminus is cleared below, never saved.
  useEffect(() => {
    if (!token) return;
    if (stage === "landing" || stage === "details") {
      saveRedemptionFlowState(token, {
        stage,
        chosenProduct,
        chosenVariantId,
        chosenLocation,
        recipientDraft,
        countryOverride,
      });
    }
  }, [
    token,
    stage,
    chosenProduct,
    chosenVariantId,
    chosenLocation,
    recipientDraft,
    countryOverride,
  ]);

  // Wipe the persisted state (shipping draft included) the instant the redemption is finished, or
  // the link turns out to be spent — nothing sensitive should outlive completion.
  useEffect(() => {
    if (!token) return;
    if (stage === "redeemed" || alreadyRedeemedAtSubmit) clearRedemptionFlowState(token);
  }, [token, stage, alreadyRedeemedAtSubmit]);

  // "Choose this gift" → snapshot the product + variant, close the detail overlay,
  // and advance to the recipient-details form.
  const handleChooseGift = (variantId: string, customLocation?: CustomLocation) => {
    if (!variantId || !productDetail.data) return;
    setChosenProduct(productForVariant(productDetail.data, variantId));
    setChosenVariantId(variantId);
    setChosenLocation(customLocation ?? null);
    setSelectedProductId(null);
    setStage("details");
  };

  // A successful redeem runs the fold-and-fly inside RedemptionDetails; it calls
  // this once the envelope has flown off. Reveal the confirmation, and linger the
  // details view one beat so it stays behind the fade-in (see tailCrossfade).
  const handleRedeemed = (contactId?: number) => {
    setRedeemedContactId(contactId);
    setStage("redeemed");
    setTailCrossfade(true);
    window.setTimeout(() => setTailCrossfade(false), 900);
  };

  if (isLoading) {
    return <p className="p-10 text-center text-graphite">Loading your gift…</p>;
  }
  // Error states only apply while there's no gift yet — once the flow has live gift data, a
  // later refetch error (e.g. a window-focus revalidation) must never swap it out.
  if (!gift) {
    const linkError = isError ? redemptionLinkError(error) : "failed";
    if (linkError !== "failed") {
      return <GiftLinkUnavailable reason={linkError} />;
    }
    return (
      <p className="p-10 text-center text-graphite">This gift link is invalid or has expired.</p>
    );
  }
  // Submit hit 410 — rendering only the redeemed page also closes every overlay.
  if (alreadyRedeemedAtSubmit) {
    return <GiftLinkUnavailable reason="already-redeemed" />;
  }
  // A self-send link opened before it goes live — valid, just dormant.
  if ("notYetActive" in gift) {
    return <RedemptionNotYetActive activationDate={gift.activationDate} />;
  }
  // A universal link whose redemption limit is used up — no gifts left to claim. Narrows `gift` to the
  // full GiftContext for everything below.
  if ("limitReached" in gift) {
    return <GiftLinkUnavailable reason="limit-reached" />;
  }

  const { config } = gift;
  // Skip Unboxing (campaign setting): the recipient lands straight on gift selection — the unbox
  // stage is never mounted, and the initial "unboxing" stage renders the landing instead.
  const showUnbox = (stage === "unboxing" && !config.skipUnboxing) || unboxLeaving;
  const showLanding = stage === "landing" || (stage === "unboxing" && config.skipUnboxing);
  // Video end and Skip share one hand-off: mount the landing underneath and crossfade the unbox
  // layer out over it (then unmount). The confetti fires from the landing-arrival effect above.
  const handleUnboxFinished = () => {
    setStage("landing");
    setUnboxLeaving(true);
    window.setTimeout(() => setUnboxLeaving(false), 800);
  };

  return (
    <main className="fixed inset-0 bg-light-dust">
      {showUnbox && (
        <div
          className="absolute inset-0 z-30 transition-opacity duration-700 ease-out"
          style={{
            opacity: unboxLeaving ? 0 : 1,
            pointerEvents: unboxLeaving ? "none" : "auto",
          }}
        >
          <Unboxing
            isFullscreen
            withSound
            isMobile={isMobile}
            partnerLogoUrl={config.unboxingLogoUrl}
            partnerLogoAlt={gift.sender.companyName ?? "Partner"}
            headline={gift.headline || buildRecipientHeadline(gift.sender)}
            showGiftMessageButton={config.showGiftMessage}
            skipUnboxing={false}
            onReadMessage={() => setGiftMsgOpen(true)}
            // Prime confetti audio inside the Unwrap gesture (iOS unlock).
            onUnwrap={primeConfettiAudio}
            playTrigger={playTrigger}
            // Natural end and Skip take the same single transition (see handleUnboxFinished).
            onVideoEnd={handleUnboxFinished}
            onSkip={handleUnboxFinished}
          />
        </div>
      )}

      {/* Landing (RFW-3) — branded hero shown after the unbox (or immediately when the campaign
          skips unboxing). Requests the shared modals via callbacks; the modals are rendered below. */}
      {showLanding && (
        <RedemptionLanding
          gift={gift}
          isMobile={isMobile}
          onReadMessage={() => setGiftMsgOpen(true)}
          onPlayVideo={() => setVideoOpen(true)}
          onOpenProduct={setSelectedProductId}
          activeCountry={activeCountry}
          onCountryChange={setCountryOverride}
        />
      )}

      {/* Recipient Details (RFW-7) — reached from the detail slide-in's "Choose this
          gift". Owns its form/review/submitting phases; a successful redeem advances
          to the "redeemed" terminus. */}
      {(stage === "details" || (stage === "redeemed" && tailCrossfade)) && chosenProduct && (
        <RedemptionDetails
          gift={gift}
          product={chosenProduct}
          variantId={chosenVariantId}
          customLocation={chosenLocation}
          shippingCountry={activeCountry}
          initialDraft={recipientDraft}
          onDraftChange={setRecipientDraft}
          onBack={() => setStage("landing")}
          // Re-open the chosen gift's detail over the details stage so the location can be
          // re-picked; "Choose this gift" then lands back here with the new one.
          onEditLocation={() => setSelectedProductId(chosenProduct.id)}
          onRedeemed={handleRedeemed}
          onAlreadyRedeemed={() => setAlreadyRedeemedAtSubmit(true)}
        />
      )}

      {/* Redeemed terminus — the thank-you note ask (RFW-9) → closing impact reveal
          + CTA (RFW-10). Fades in over the folded-away details. */}
      {stage === "redeemed" && (
        <RedemptionSuccess
          gift={gift}
          token={token ?? ""}
          contactId={redeemedContactId}
          product={chosenProduct ?? undefined}
          recipientFirstName={recipientDraft?.firstName}
        />
      )}

      {/* Shared gift-message modal — top level so it sits above whichever stage is
          active. The "Unwrap Gift" CTA only appears on the unboxing stage (it
          replays the unbox video); the landing shows the message alone. */}
      {giftMsgOpen && (
        <GiftMessageModal
          message={gift.message}
          isMobile={isMobile}
          onClose={() => setGiftMsgOpen(false)}
          onUnwrap={
            stage === "unboxing"
              ? () => {
                  setGiftMsgOpen(false);
                  setPlayTrigger((t) => t + 1);
                }
              : undefined
          }
        />
      )}

      {/* Shared gift video-message modal (landing banner play). */}
      <GiftVideoModal
        open={videoOpen}
        videoUrl={config.videoUrl}
        onClose={() => setVideoOpen(false)}
      />

      {/* Product detail — desktop: right slide-in; mobile: bottom "gift detail sheet".
          Opens on the Impact tab; a card tap in the collection sets the selected id. */}
      <ProductDetailSlideIn
        open={selectedProductId !== null}
        product={productDetail.data ?? null}
        loading={productDetail.isLoading}
        error={productDetail.isError}
        initialTab="impact"
        mobilePresentation="sheet"
        onClose={() => setSelectedProductId(null)}
        onSendGift={handleChooseGift}
      />

      {/* Persistent confetti overlay — fires on the unbox "arrival" and keeps
          playing even after later stages mount (fixed, pointer-events:none).
          Shape + color scheme come from the campaign's Branding config; absent or
          `enabled: false` hides it (absent ⇒ enabled, mixed, brand palette). */}
      {config.confetti?.enabled !== false && (
        <Confetti
          trigger={confettiTrigger}
          shape={config.confetti?.shape}
          colors={config.confetti?.colors ?? resolveSchemeColors(config.confetti?.scheme ?? null)}
        />
      )}
    </main>
  );
}
